import { Component, computed, inject, output, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FieldTree, form, FormField, ReadonlyFieldState } from '@angular/forms/signals';
import {
	InputMode,
	RadioOption,
	UiButtonComponent,
	UiInputNumberComponent,
	UiRadioGroupComponent,
	UiSwitchToggleComponent,
	UIIconComponent
} from '@design-system';
import { PriceFromHeadlineSide, TickType } from '@setting';
import { UserSettingEventProducerService } from '../../../../events/user-setting/user-setting-event-producer.service';
import { DEFAULT_ORDER_SETTINGS, OrderSettings } from '../../../../models/order-settings.model';
import { orderSettingsSchema } from '../../../../models/order-settings.schema';
import { BottomSheetComponent } from '../../../../components/bottom-sheet/bottom-sheet.component';

const TICK_TYPE_OPTIONS: RadioOption[] = [
	{ label: 'درصد', value: TickType.Percent },
	{ label: 'مقدار', value: TickType.Amount }
];

const PRICE_FROM_HEADLINE_SIDE_OPTIONS: RadioOption[] = [
	{ label: 'برترین مظنه‌ی سمت سفارش', value: PriceFromHeadlineSide.Same },
	{ label: 'برترین مظنه‌ی سمت مقابل سفارش', value: PriceFromHeadlineSide.Opposite }
];

/** Surface a field's first validation message, but only once the user has engaged with it. */
function visibleError(state: ReadonlyFieldState<unknown>): string | undefined {
	if (!state.touched() && !state.dirty()) {
		return undefined;
	}
	return state.errors()[0]?.message;
}

/**
 * `ui-input-number` emits a display string (thousand separators, a trailing `.`
 * while typing a decimal, `''` when cleared). Anything that is not a real
 * number becomes `NaN` so the schema reports it instead of it reading as zero.
 */
function parseNumericInput(value: string | number): number {
	if (typeof value === 'number') {
		return value;
	}

	const normalized = value.replace(/,/g, '').trim();
	return normalized === '' ? Number.NaN : Number(normalized);
}

@Component({
	selector: 'app-order-settings-bottom-sheet',
	imports: [
		BottomSheetComponent,
		FormField,
		FormsModule,
		UiButtonComponent,
		UiInputNumberComponent,
		UiRadioGroupComponent,
		UiSwitchToggleComponent,
		UIIconComponent
	],
	templateUrl: './order-settings-bottom-sheet.component.html',
	host: {
		'data-cy': 'order-settings-bottom-sheet'
	}
})
export class OrderSettingsBottomSheetComponent {
	private readonly userSetting = inject(UserSettingEventProducerService);

	readonly saved = output<OrderSettings>();

	private readonly sheet = viewChild<BottomSheetComponent>('sheet');
	private readonly tickHelpSheet = viewChild<BottomSheetComponent>('tickHelpSheet');

	private readonly model = signal<OrderSettings>({ ...DEFAULT_ORDER_SETTINGS });

	readonly settingsForm = form(this.model, orderSettingsSchema);

	readonly saving = this.userSetting.savingOrderSettings;
	readonly saveError = this.userSetting.orderSaveError;

	readonly tickTypeOptions = TICK_TYPE_OPTIONS;
	readonly priceFromHeadlineSideOptions = PRICE_FROM_HEADLINE_SIDE_OPTIONS;
	readonly InputMode = InputMode;

	readonly buyQuantityError = computed(() => visibleError(this.settingsForm.buyQuantity()));
	readonly sellQuantityError = computed(() => visibleError(this.settingsForm.sellQuantity()));
	readonly tickError = computed(() => visibleError(this.settingsForm.tick()));

	/** Only meaningful while the headline switch is on. */
	readonly showHeadlineSideOptions = computed(() => this.settingsForm.priceFromHeadline().value());

	/** Stepper increment follows the unit: fine for a percentage, coarse for an amount. */
	readonly tickStep = computed(() => (this.settingsForm.tickType().value() === TickType.Percent ? 0.1 : 1));

	readonly canSave = computed(() => this.settingsForm().valid() && this.settingsForm().dirty() && !this.saving());

	private divertingToHelp = false;

	/** Reload the persisted settings into a clean form, then present the sheet. */
	async open(): Promise<void> {
		const settings = await this.userSetting.loadOrderSettings();
		this.settingsForm().reset({ ...settings });
		await (this.sheet()?.open() ?? Promise.resolve());
	}

	close(): Promise<void> {
		return this.sheet()?.close() ?? Promise.resolve();
	}

	/**
	 * Bridge for `ui-input-number`, which cannot take `[formField]`: its
	 * `min` / `max` / `minLength` / `maxLength` inputs are non-optional, so the
	 * template type-checker rejects the validation metadata the directive binds.
	 * The value still lands on the Signal Form field, so the schema validates it.
	 */
	onNumberInput(field: FieldTree<number>, value: string | number): void {
		const state = field();
		const parsed = parseNumericInput(value);

		// `ui-input-number` also emits from `writeValue`, so binding the field
		// would otherwise mark the form dirty before the user has typed anything.
		if (Object.is(parsed, state.value())) {
			return;
		}

		state.value.set(parsed);
		state.markAsDirty();
	}

	/** Mirrors the touched-on-blur behavior `[formField]` provides for free. */
	onNumberBlur(field: FieldTree<number>): void {
		field().markAsTouched();
	}

	async onSaveClick(): Promise<void> {
		this.settingsForm().markAsTouched();

		if (this.settingsForm().invalid()) {
			return;
		}

		const persisted = await this.userSetting.saveOrderSettings(this.model());
		if (!persisted) {
			return;
		}

		this.saved.emit(this.userSetting.orderSettings());
		await this.close();
	}

	async onCancelClick(): Promise<void> {
		this.discardEdits();
		await this.close();
	}

	onSheetClosed(): void {
		if (this.divertingToHelp) {
			this.divertingToHelp = false;
			return;
		}
		this.discardEdits();
	}

	async onTickHelpClick(): Promise<void> {
		this.divertingToHelp = true;
		await this.close();
		await (this.tickHelpSheet()?.open() ?? Promise.resolve());
	}

	async onTickHelpClosed(): Promise<void> {
		await (this.sheet()?.open() ?? Promise.resolve());
	}

	private discardEdits(): void {
		this.settingsForm().reset({ ...this.userSetting.orderSettings() });
		this.userSetting.clearOrderSaveError();
	}
}
